import {
  lighten,
  Tooltip as MuiTooltip,
  Stack,
  SvgIcon,
  styled,
  type TooltipProps,
  Typography,
  tooltipClasses,
} from '@mui/material';
import { useIsMobile } from '@side/pantry';
import { useAtomValue, useSetAtom } from 'jotai';
import { forwardRef, useCallback, useMemo } from 'react';
import { hoveredTeamId } from '@/common/atoms/community';
import { MAP_PIN_POPPER_MODIFIERS } from '@/views/Community/constants';

const Tooltip = styled(({ className, ...props }: TooltipProps) => (
  <MuiTooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.text.primary,
    boxShadow: theme.shadows[3],
    width: 260,
  },
  '&[data-popper-reference-hidden]': {
    visibility: 'hidden',
    pointerEvents: 'none',
  },
}));

const StyledSvgIcon = styled(SvgIcon)(({ theme }) => ({
  width: 32,
  height: 32,
  path: {
    fill: theme.palette.secondary.main,
    stroke: theme.palette.secondary.dark,
  },
  circle: {
    fill: theme.palette.common.white,
  },
  '&:hover, &.active, &.selected': {
    path: {
      fill: theme.palette.secondary.main,
    },
    circle: {
      fill: theme.palette.secondary.dark,
    },
  },
  '&.selected': {
    transform: 'scale(1.25)',
    transformOrigin: 'bottom',
    // Can't use theme.shadows since it generates a syntax that is invalid for drop-shadow
    filter: 'drop-shadow(0px 3px 2px rgba(0,0,0,0.4))',
  },
  '&.outOfBounds': {
    path: {
      fill: lighten(theme.palette.secondary.main, 0.4),
      stroke: theme.palette.secondary.main,
    },
    circle: {
      fill: theme.palette.common.white,
    },
    '&:hover, &.active, &.selected': {
      circle: {
        fill: theme.palette.secondary.dark,
      },
    },
  },
}));

const popperProps = { modifiers: MAP_PIN_POPPER_MODIFIERS };

export const MapPin = forwardRef<
  SVGSVGElement,
  {
    className: string;
    teamId: string;
    teamName: string | null;
    alias: string | null;
    address: string | null;
    onHover: () => unknown;
  }
>(({ className, teamId, teamName, address, onHover, alias }, ref) => {
  const setHoveredTeam = useSetAtom(hoveredTeamId);
  const hoveredId = useAtomValue(hoveredTeamId);
  const isHovered = hoveredId === teamId;
  const isMobile = useIsMobile('md');

  const title = useMemo(
    () => (
      <Stack
        sx={{
          gap: 0.5,
          py: 1.5,
          px: 1,
          alignItems: 'flex-start',
        }}
      >
        <Typography variant="h5">{teamName ?? alias}</Typography>
        {teamName && alias && (
          <Typography
            variant="caption"
            sx={{ pt: 0.5, pb: 1, color: 'text.secondary' }}
          >
            {alias}
          </Typography>
        )}
        <Typography variant="body3">{address}</Typography>
      </Stack>
    ),
    [teamName, address, alias],
  );

  const handleMouseEnter = useCallback(() => {
    setHoveredTeam(teamId);
    onHover();
  }, [onHover, setHoveredTeam, teamId]);
  const handleMouseLeave = useCallback(
    () => setHoveredTeam(undefined),
    [setHoveredTeam],
  );

  return (
    <Tooltip
      open={isHovered && !isMobile}
      placement="top"
      slotProps={{ popper: popperProps }}
      title={title}
    >
      <StyledSvgIcon
        className={className}
        data-testid="MapPinIcon"
        ref={ref}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <svg
          width="32"
          height="32"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path d="M16.0001 2.66699C10.8401 2.66699 6.66675 6.84033 6.66675 12.0003C6.66675 19.0003 16.0001 29.3337 16.0001 29.3337C16.0001 29.3337 25.3334 19.0003 25.3334 12.0003C25.3334 6.84033 21.1601 2.66699 16.0001 2.66699ZM16.0001 15.3337C14.1601 15.3337 12.6667 13.8403 12.6667 12.0003C12.6667 10.1603 14.1601 8.66699 16.0001 8.66699C17.8401 8.66699 19.3334 10.1603 19.3334 12.0003C19.3334 13.8403 17.8401 15.3337 16.0001 15.3337Z" />
          <circle cx="15.9996" cy="11.8707" r="3.6129" />
        </svg>
      </StyledSvgIcon>
    </Tooltip>
  );
});

MapPin.displayName = 'MapPin';
